import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Container, Main, Flex } from '../../components/styled/Layout';
import { Card, CardContent } from '../../components/styled/Card';
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell } from '../../components/styled/Table';
import { SecondaryButton } from '../../components/styled/Button';
import LoadingOverlay from '../../components/LoadingOverlay';
import hestonService from './services/hestonService';
import './styles/HestonApp.css';

export default function HestonApp() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await hestonService.listProjects();
      setProjects(data);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProject = (projectId) => {
    navigate(`/heston/projects/${projectId}`);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedProjects = [...projects].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle null/undefined
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // Convert dates for comparison
    if (sortField === 'createdAt' || sortField === 'updatedAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    // Convert to lowercase for string comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return <LoadingOverlay message="Loading projects..." />;
  }

  return (
    <Container>
      <Main>
        <Flex direction="column" gap="var(--space-5)">
          {/* Header */}
          <Flex direction="column" gap="var(--space-3)">
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#2C3E50', 
              margin: 0
            }}>
              HESTON Project Management
            </h1>
          </Flex>

          {/* Error Message */}
          {error && (
            <Card variant="outlined" style={{ borderColor: '#C0392B', background: '#F8D7DA' }}>
              <CardContent>
                <p style={{ color: '#721C24', margin: 0 }}>{error}</p>
              </CardContent>
            </Card>
          )}

          {/* Projects Table */}
          {projects.length === 0 ? (
            <Card>
              <CardContent style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
                <h3 style={{ 
                  fontSize: '18px', 
                  color: '#2C3E50', 
                  marginBottom: '8px' 
                }}>
                  No Projects Available
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#666666'
                }}>
                  Contact your administrator to get access to projects.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent style={{ padding: 0 }}>
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHeader>
                      <tr>
                        <TableHeaderCell 
                          onClick={() => handleSort('name')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Project Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell 
                          onClick={() => handleSort('description')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Description {sortField === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell 
                          onClick={() => handleSort('addressCount')}
                          style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                        >
                          Addresses {sortField === 'addressCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell 
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Created {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell 
                          onClick={() => handleSort('updatedAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Last Updated {sortField === 'updatedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>Actions</TableHeaderCell>
                      </tr>
                    </TableHeader>
                    <TableBody>
                      {sortedProjects.map((project) => (
                        <TableRow key={project.projectId}>
                          <TableCell style={{ fontWeight: '600', color: '#0077B5' }}>
                            {project.name}
                          </TableCell>
                          <TableCell style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.description || <span style={{ color: '#999', fontStyle: 'italic' }}>No description</span>}
                          </TableCell>
                          <TableCell style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {project.addressCount || 0}
                          </TableCell>
                          <TableCell style={{ whiteSpace: 'nowrap' }}>
                            {new Date(project.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell style={{ whiteSpace: 'nowrap' }}>
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell style={{ textAlign: 'center' }}>
                            <SecondaryButton
                              onClick={() => handleViewProject(project.projectId)}
                              style={{ padding: '6px 12px', fontSize: '13px' }}
                            >
                              View
                            </SecondaryButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </Flex>
      </Main>
    </Container>
  );
}
